import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { actionPermissions } from '../../../constants/actionPermissions';
import { GREEN1, PRIMARY, RED1 } from '../../../constants/color';
import { dashboardRoute } from '../../../constants/pathname';
import { useTab } from '../../../hooks/useTab';
import { OrderClaimData } from '../../../types';
import { hasPermission } from '../../../utils/hasPermission';
import getDashboardStyle from '../../../utils/getDashboardStyle';
import { postDataWithAuthToken } from '../../../utils/axiosRequest';
import { alertMessage } from '../../../utils/alertMessage';

type ClaimBasicInfoProps = {
  orderClaimDetail: OrderClaimData;
  onRefresh?: () => void;
};

/**
 * Displays Claim Information
 *
 */
const ClaimBasicInfo = ({
  orderClaimDetail,
  onRefresh,
}: ClaimBasicInfoProps) => {
  const { t } = useTranslation();
  const isSubscribed = useRef(true);
  const { addTab } = useTab();
  const [isLoading, setIsLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();

  // Sets isSubscribed to false if component becomes unmounted
  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  const handleOnEdit = () => {
    if (editing) {
      form
        .validateFields()
        .then((values) => {
          setIsLoading(true);
          postDataWithAuthToken('claim/edit', {
            claimId: orderClaimDetail.id,
            claimReason: values.claimReason,
            remark: values.remark,
          })
            .then((response) => {
              if (response && response.goodStatus) {
                alertMessage('success', t('order.alerts.orderEdited'));
                handleOnCancel();
                if (onRefresh) onRefresh();
              } else {
                alertMessage(
                  'error',
                  response?.msg || t('general.noResponse'),
                  response?.data || undefined
                );
              }
              setIsLoading(false);
            })
            .catch((err) => {
              setIsLoading(false);
              console.log(err);
            });
        })
        .catch((err) => console.log(err));
    } else {
      setEditing(true);
    }
  };

  const handleOnCancel = () => {
    form.resetFields();
    setEditing(false);
  };

  return (
    <Spin spinning={isLoading}>
      <Form layout="vertical" form={form} initialValues={orderClaimDetail}>
        <Row gutter={[16, 24]}>
          <Col
            span={24}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography.Text strong style={{ fontSize: 16 }}>
              {t('order.claimListColumns.overviewTitle')}
            </Typography.Text>
            {editing ? (
              <Space>
                <Button onClick={handleOnCancel}>{t('general.cancel')}</Button>
                <Button
                  disabled={
                    !hasPermission(actionPermissions.orderGroup.orderManage)
                  }
                  type="primary"
                  onClick={handleOnEdit}
                >
                  {t('general.ok')}
                </Button>
              </Space>
            ) : (
              <Button
                disabled={
                  !hasPermission(actionPermissions.orderGroup.orderManage)
                }
                type="text"
                icon={<EditOutlined />}
                onClick={handleOnEdit}
              />
            )}
          </Col>
          {orderClaimDetail.id && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.id')}: `}
                >
                  {orderClaimDetail.id}
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.user && orderClaimDetail.user.userName && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.username')}: `}
                >
                  <Space style={{ flexDirection: 'column' }}>
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      disabled={
                        !hasPermission(actionPermissions.userGroup.userView)
                      }
                      onClick={() =>
                        addTab(
                          '',
                          `${dashboardRoute.users.detail}?user_id=${
                            orderClaimDetail.user
                              ? orderClaimDetail.user.userId
                              : ''
                          }`
                        )
                      }
                    >
                      {orderClaimDetail.user
                        ? orderClaimDetail.user.userName
                        : ''}
                    </Button>
                    {(orderClaimDetail.user?.userRank ||
                      orderClaimDetail.user?.member) && (
                      <Space>
                        {orderClaimDetail.user?.userRank && (
                          <Tag color="geekblue">
                            {orderClaimDetail.user?.userRank.rankName}
                          </Tag>
                        )}
                        {orderClaimDetail.user?.member && (
                          <Tag color="red">
                            {orderClaimDetail.user?.member.rankName}
                          </Tag>
                        )}
                      </Space>
                    )}
                  </Space>
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.seller && orderClaimDetail.seller.shopName && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.shopName')}: `}
                >
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => {
                      if (
                        hasPermission(
                          actionPermissions.sellerGroup.sellerMerchants
                        )
                      )
                        addTab(
                          '',
                          `${dashboardRoute.sellers.detail}?seller_id=${orderClaimDetail.seller.sellerId}`
                        );
                    }}
                  >
                    {orderClaimDetail.seller.shopName}
                  </Button>
                </Form.Item>
              }
            </Col>
          )}
          <Col span={24} md={12} lg={8} xl={6}>
            {
              <Form.Item
                style={{ marginBottom: 0 }}
                label={`${t('order.claimListColumns.deleteStatus')}: `}
              >
                {orderClaimDetail.isDelete ? (
                  <CloseOutlined style={{ color: RED1 }} />
                ) : (
                  <CheckOutlined style={{ color: GREEN1 }} />
                )}
              </Form.Item>
            }
          </Col>
          {orderClaimDetail.adminUser &&
            orderClaimDetail.adminUser.adminUserName && (
              <Col span={24} md={12} lg={8} xl={6}>
                {
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    label={`${t('order.claimListColumns.adminUserName')}: `}
                  >
                    <div style={{ wordBreak: 'break-all' }}>
                      {orderClaimDetail.adminUser.adminUserName}
                    </div>
                  </Form.Item>
                }
              </Col>
            )}
          {orderClaimDetail.claimSn && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.claimSn')}: `}
                >
                  <div style={{ wordBreak: 'break-all' }}>
                    {orderClaimDetail.claimSn}
                  </div>
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.claimSn && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.claimInvoiceSn')}: `}
                >
                  <div style={{ wordBreak: 'break-all', color: PRIMARY }}>
                    {orderClaimDetail.claimInvoiceSn}
                  </div>
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.claimAmount && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.claimAmount')}: `}
                >
                  {orderClaimDetail.claimAmount}
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.finalAmount && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.finalAmount')}: `}
                >
                  {orderClaimDetail.finalAmount}
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.claimType && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.claimType')}: `}
                >
                  <div style={{ wordBreak: 'break-word' }}>
                    {orderClaimDetail.claimType.description}
                  </div>
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.refundMethod && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.refundMethod')}: `}
                >
                  <div style={{ wordBreak: 'break-word' }}>
                    {orderClaimDetail.refundMethod.description}
                  </div>
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.refundTargetId > 0 && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.orderDetail.refundTargetId')}: `}
                >
                  <Typography.Text>
                    {orderClaimDetail.refundTargetId}
                  </Typography.Text>
                </Form.Item>
              }
            </Col>
          )}
          {orderClaimDetail.claimReason && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.claimReason')}: `}
                  name={editing ? 'claimReason' : undefined}
                >
                  {editing ? (
                    <Input.TextArea autoSize={{ minRows: 4 }} />
                  ) : (
                    <div style={{ wordBreak: 'break-word' }}>
                      {orderClaimDetail.claimReason}
                    </div>
                  )}
                </Form.Item>
              }
            </Col>
          )}
          <Col span={24} md={12} lg={8} xl={6}>
            {
              <Form.Item
                style={{ marginBottom: 0 }}
                label={`${t('order.claimListColumns.isRead')}: `}
              >
                {orderClaimDetail.isRead ? (
                  <CheckOutlined style={{ color: GREEN1 }} />
                ) : (
                  <CloseOutlined style={{ color: RED1 }} />
                )}
              </Form.Item>
            }
          </Col>
          {orderClaimDetail.readTime && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.readTime')}: `}
                >
                  <div style={{ wordBreak: 'break-word' }}>
                    {orderClaimDetail.readTime}
                  </div>
                </Form.Item>
              }
            </Col>
          )}

          {getDashboardStyle().isWholeSaleAppType &&
            orderClaimDetail.claimType?.code === 'CREDIT_MEMO' && (
            <>
              <Col span={24} md={12} lg={8} xl={6}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.isUsedStatus')}: `}
                >
                  {orderClaimDetail.isUsed ? (
                    <CheckOutlined style={{ color: GREEN1 }} />
                  ) : (
                    <CloseOutlined style={{ color: RED1 }} />
                  )}
                </Form.Item>
              </Col>
              {orderClaimDetail.isUsed && (
                <Col span={24} md={12} lg={8} xl={6}>
                  {
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      label={`${t('order.claimListColumns.usedTime')}: `}
                    >
                      {orderClaimDetail.usedTime}
                    </Form.Item>
                  }
                </Col>
              )}
            </>
          )}
          {orderClaimDetail.readTime && (
            <Col span={24} md={12} lg={8} xl={6}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.readTime')}: `}
                >
                  <div style={{ wordBreak: 'break-word' }}>
                    {orderClaimDetail.readTime}
                  </div>
                </Form.Item>
              }
            </Col>
          )}
          <Col span={24} md={12} lg={8} xl={6}>
            {
              <Form.Item
                style={{ marginBottom: 0 }}
                label={`${t('order.claimListColumns.addTime')}: `}
              >
                <div style={{ wordBreak: 'break-word' }}>
                  {orderClaimDetail.addTime}
                </div>
              </Form.Item>
            }
          </Col>
          {orderClaimDetail.remark && (
            <Col span={24} md={12} lg={8} xl={6}>
              <Form.Item
                style={{ marginBottom: 0 }}
                label={`${t('order.claimListColumns.remark')}: `}
                name={editing ? 'remark' : undefined}
              >
                {editing ? (
                  <Input.TextArea autoSize={{ minRows: 4 }} />
                ) : (
                  <div style={{ wordBreak: 'break-word' }}>
                    {orderClaimDetail.remark}
                  </div>
                )}
              </Form.Item>
            </Col>
          )}
          {orderClaimDetail.udeskLink && (
            <Col span={24} md={12} lg={12} xl={12}>
              {
                <Form.Item
                  style={{ marginBottom: 0 }}
                  label={`${t('order.claimListColumns.udeskLink')}: `}
                >
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => window.open(orderClaimDetail.udeskLink)}
                  >
                    {orderClaimDetail.udeskLink}
                  </Button>
                </Form.Item>
              }
            </Col>
          )}
        </Row>
      </Form>
    </Spin>
  );
};

export default ClaimBasicInfo;
